import React from 'react';
import ErrorBoundary from '../../components/ErrorBoundary';
import { formatCalendarDay } from '../../utils/localDate';
import { splitSummary } from './splitParts';
import { signedAmountText, directionClass, needsDirection, suggestedDirection } from '../../utils/transactionDirection';

// One row of the transactions table. Notes show under the description.
export default function TransactionRow({ transaction, formatCurrency, onEdit, onDelete, onSplit = null, onSetDirection = null }) {
  const unknown = needsDirection(transaction);
  const hint = unknown ? suggestedDirection(transaction.description) : null;
  return (
    <tr className="hover:bg-gray-50">
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCalendarDay(transaction.transaction_date)}</td>
      <td className="px-6 py-4 text-sm text-gray-900">
        {transaction.description}
        {transaction.notes ? (
          <div className="text-xs text-gray-500 mt-1 whitespace-pre-line" title="Notes">{transaction.notes}</div>
        ) : null}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
        {transaction.subsidiary_name ? (
          <span className="text-blue-600 font-medium">
            {transaction.subsidiary_name}
            <span className="text-gray-500 text-xs ml-1">({transaction.subsidiary_state})</span>
          </span>
        ) : (
          <span className="text-gray-600">{transaction.business_name} (Parent)</span>
        )}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
        {transaction.splits?.length ? (
          <span title={splitSummary(transaction.splits)} className="text-purple-700">Split ({transaction.splits.length})</span>
        ) : transaction.category_name}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{transaction.account_name}</td>
      <ErrorBoundary>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
          {transaction.plaid_transaction_id ? (
          <div className="text-xs text-gray-500 font-mono break-all" title="Bank transaction ID">Bank ID: {transaction.plaid_transaction_id}</div>
        ) : null}
        {transaction.reference_number ? (
            <span className="text-xs font-mono bg-gray-100 px-2 py-1 rounded">{transaction.reference_number}</span>
          ) : (
            <span className="text-gray-400 text-xs">—</span>
          )}
        </td>
      </ErrorBoundary>
      <td className={`px-6 py-4 whitespace-nowrap text-sm text-right font-semibold ${directionClass(transaction)}`}>
        {signedAmountText(transaction, formatCurrency)}
        {unknown && (
          <div className="text-xs font-normal text-amber-700 mt-1" title="The bank's in/out was not saved for this transfer. It counts toward no balance until set.">
            Needs review{hint ? ` (looks ${hint === 'in' ? 'In' : 'Out'})` : ''}
            {onSetDirection && ['in', 'out'].map((d) => (
              <button key={d} type="button" onClick={() => onSetDirection(transaction, d)}
                className={`ml-2 underline ${hint === d ? 'font-semibold' : ''}`}>{d === 'in' ? 'In' : 'Out'}</button>
            ))}
          </div>
        )}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
        {onSplit && <button onClick={() => onSplit(transaction)} className="text-purple-600 hover:text-purple-800 mr-3">Split</button>}
        <button onClick={() => onEdit(transaction)} className="text-blue-600 hover:text-blue-800 mr-3">Edit</button>
        <button onClick={() => onDelete(transaction.id)} className="text-red-600 hover:text-red-800">Delete</button>
      </td>
    </tr>
  );
}
